import React from 'react';
import { Pressable, View } from 'react-native';

import Text from '../../components/text/Text';
import { useThemedStyles } from '../../hooks/useThemedStyles';

/**
 * SegmentedControl: one choice out of 2-4 options shown side by side
 * (Mine / All, Purchase orders / Stock deliveries, Grid / List). The selected
 * segment uses the current-step colours so it is clear in every theme.
 * Props:
 * - options: [{ value, label, accessibilityLabel }]
 * - value: the selected option's value; onChange(value) (not called for the selected one)
 * - role: 'button' (default) | 'tab' (the control switches tabs; the track is a tablist)
 * - style
 */
const SegmentedControl = ({ options, value, onChange, role = 'button', style }) => {
	const styles = useThemedStyles(createStyles);

	return (
		<View style={[styles.track, style]} accessibilityRole={role === 'tab' ? 'tablist' : undefined}>
			{options.map((option) => {
				const isSelected = option.value === value;
				return (
					<Pressable
						key={option.value}
						onPress={() => !isSelected && onChange(option.value)}
						accessibilityRole={role}
						accessibilityLabel={option.accessibilityLabel || option.label}
						accessibilityState={{ selected: isSelected }}
						style={({ pressed }) => [styles.segment, isSelected && styles.segmentSelected, pressed && styles.pressed]}
					>
						<Text style={[styles.label, isSelected && styles.labelSelected]} numberOfLines={1}>{option.label}</Text>
					</Pressable>
				);
			})}
		</View>
	);
};

const createStyles = ({ ui: { colors, radius, space, touch, type } }) => ({
	track: {
		flexDirection: 'row',
		padding: space.xs,
		gap: space.xs,
		borderRadius: radius.pill,
		borderWidth: 1,
		borderColor: colors.border,
		backgroundColor: colors.card,
	},
	segment: {
		flex: 1,
		minHeight: touch.min,
		alignItems: 'center',
		justifyContent: 'center',
		// Tight padding and 13 pt labels so three segments fit at phone width ("Machine setups").
		paddingHorizontal: space.xs,
		borderRadius: radius.pill,
		borderWidth: 1,
		borderColor: 'transparent',
	},
	segmentSelected: {
		backgroundColor: colors.current,
		borderColor: colors.currentBorder,
	},
	pressed: {
		opacity: 0.8,
	},
	label: {
		...type.secondaryStrong,
		color: colors.textSecondary,
	},
	labelSelected: {
		...type.secondarySemibold,
		color: colors.text,
	},
});

export default SegmentedControl;
